export enum OrganizationType {
    RECYCLER = 'recycler',
    TRUCK = 'truck',
    WASTE_OUTLET = 'waste_outlet',
    CHARITY = 'charity',
}

export const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
    [OrganizationType.RECYCLER]: 'Recycler',
    [OrganizationType.TRUCK]: 'Waste Mover',
    [OrganizationType.WASTE_OUTLET]: 'Waste Outlet',
    [OrganizationType.CHARITY]: 'Charity',
};

/** Waste Outlet + Charity — shared site/container/pickup surfaces. */
export const SITE_OPS_ORG_TYPES: readonly OrganizationType[] = [
    OrganizationType.WASTE_OUTLET,
    OrganizationType.CHARITY,
] as const;

export function getOrganizationTypeLabel(type: OrganizationType): string {
    return ORGANIZATION_TYPE_LABELS[type] ?? type;
}

/**
 * Site/container operations orgs (branches, pickups, container map).
 * Charity mirrors Waste Outlet operationally.
 */
export function isSiteOperationsOrgType(type: OrganizationType): boolean {
    return type === OrganizationType.WASTE_OUTLET || type === OrganizationType.CHARITY;
}

/**
 * Orgs that participate in site ops but never expose real money values
 * (sale amounts, collection costs, invoices, etc.).
 */
export function isNonFinancialOrgType(type: OrganizationType): boolean {
    return type === OrganizationType.CHARITY;
}

/** Display/submit financial amounts — always 0 for non-financial org types. */
export function financialAmountForOrg(
    orgType: OrganizationType | null | undefined,
    amount: number | null | undefined,
): number {
    if (orgType != null && isNonFinancialOrgType(orgType)) {
        return 0;
    }
    return Number(amount ?? 0);
}

export interface OrganizationRequest {
    name: string;
    organization_type: OrganizationType;
    google_maps_url: string;
    data: Record<string, any>;
}

export interface OrganizationSchema {
    id: string;
    name: string;
    organization_type: OrganizationType;
    google_maps_url: string;
    data: Record<string, any>;
    created_at: string;
    updated_at: string;
}

export interface UpdateOrganizationRequest {
    name?: string;
    organization_type?: OrganizationType;
    google_maps_url?: string;
    data?: Record<string, any>;
}

export interface OrganizationPagination {
    organizations: OrganizationSchema[];
    total: number;
    page: number;
    limit: number;
}

export interface OrganizationQueryParams {
    page?: number;
    limit?: number;
    name?: string | null;
    organization_type?: OrganizationType | null;
}
